/**
 * The one place that says where each investor-profile edit is stored.
 *
 * WHY THIS MODULE EXISTS
 * ----------------------
 * `handleSaveEdit` in `(investor)/investorprofile.tsx` carried TWO parallel
 * switch statements over the same `section` value: one patching the Zustand
 * store, one building `dbUpdates`. Anything present in the first and absent
 * from the second looked saved and wasn't.
 *
 * That is not hypothetical — it is how the entire feature failed:
 *
 *   - `thesis` and `social` had no `dbUpdates` branch at all, and no columns.
 *   - `bio` wrote `bio` but not `headline`, though the column existed and the
 *     headline is the line rendered under the investor's name.
 *   - `valueAdd` wrote the prose and dropped expertise, office hours and
 *     board seats.
 *   - `thesis` updated `investment_focus` / `stage_preference` in the store
 *     while the columns of the same name went untouched.
 *
 * Because the store is persisted AND reset on sign-out, the result was an
 * investor writing their thesis, signing out, and losing it silently — there
 * was never a write to fail.
 *
 * One mapping, one direction. `profile-edit-fields.test.ts` asserts every
 * field the editor renders is accounted for here, so adding a field to
 * `ProfileEditModal` without giving it a home fails CI instead of shipping.
 */

/**
 * Every field `ProfileEditModal` renders for the investor sections, keyed by
 * section. Mirrors `renderSectionContent()` in that component — the test pins
 * the two together.
 */
export const INVESTOR_EDITOR_FIELDS = {
  user: ['name'],
  basic: ['firm', 'location', 'yearsInvesting'],
  bio: ['headline', 'bio', 'previousRoles'],
  thesis: [
    'thesisStatement',
    'sweetSpot',
    'sectors',
    'stagePreference',
    'teamPreferences',
    'dealBreakers',
  ],
  ticketSize: ['min', 'max'],
  valueAdd: ['expertiseAreas', 'description', 'officeHours', 'boardExperience'],
  social: ['twitter', 'website', 'calendly', 'substack'],
} as const;

export type InvestorEditSection = keyof typeof INVESTOR_EDITOR_FIELDS;

/** One entry of `investor_profiles.previous_roles`. */
export interface PreviousRole {
  title: string;
  company: string;
  years?: string;
}

/**
 * Drop rows the user started and left blank, and trim what survives.
 *
 * A row needs at least a title or a company to mean anything; `years` is
 * optional because "VP Product at Stripe" is still a fact without a date.
 */
function cleanRoles(value: unknown[]): PreviousRole[] {
  return value
    .map((row) => (row && typeof row === 'object' ? (row as Record<string, unknown>) : {}))
    .map((row) => ({
      title: String(row.title ?? '').trim(),
      company: String(row.company ?? '').trim(),
      years: String(row.years ?? '').trim(),
    }))
    .filter((row) => row.title || row.company)
    .map((row) => (row.years ? row : { title: row.title, company: row.company }));
}

/**
 * Fields the editor once rendered that MUST NOT be written from here.
 *
 * `email` is owned by the auth provider; changing it is an auth flow, not a
 * profile patch, and the input silently did nothing.
 *
 * `role` is the dangerous one. In the store it means a job title ("Managing
 * Partner"), but the column of that name — `profiles.role` — is the
 * authorization trust boundary (`startup` | `investor` | `admin`), guarded by
 * a trigger precisely because a client-writable path into it is privilege
 * escalation. The two are unrelated concepts sharing a word. The job title a
 * founder reads is `headline`, which the `bio` section already owns, so the
 * field was removed from the editor rather than given a column.
 */
export const INVESTOR_EDITOR_REMOVED_FIELDS = ['email', 'role'] as const;

const trimmedOrNull = (v: unknown): string | null => {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t === '' ? null : t;
};

const cleanArray = (v: unknown): string[] =>
  Array.isArray(v) ? v.map((s) => String(s).trim()).filter(Boolean) : [];

const intOrNull = (v: unknown): number | null => {
  const n = parseInt(String(v ?? ''), 10);
  return Number.isFinite(n) ? n : null;
};

/**
 * Columns to write to `investor_profiles` for a saved section.
 *
 * Returns `{}` for `user`, whose name lands in `profiles` (and is mirrored to
 * `investor_profiles.name`) through a different call — see
 * `buildInvestorNameUpdate`.
 */
export function buildInvestorProfileDbUpdate(
  section: InvestorEditSection,
  data: Record<string, unknown>
): Record<string, unknown> {
  switch (section) {
    case 'user':
      return {};

    case 'basic':
      return {
        firm: trimmedOrNull(data.firm),
        location: trimmedOrNull(data.location),
        years_investing: intOrNull(data.yearsInvesting),
      };

    case 'bio':
      return {
        // `headline` existed as a column the whole time and was never written.
        headline: trimmedOrNull(data.headline),
        bio: trimmedOrNull(data.bio),
        // `previous_roles` is jsonb, and was equally never written from the app:
        // the only writer was the LinkedIn enrichment edge function, which fills
        // empty fields only and needs an API key — so the Experience list was
        // blank for anyone it had not run for, with no way to type one in.
        //
        // The key is OMITTED when the caller did not supply it, rather than
        // written as []. An update object without the key leaves the column
        // alone; one with [] would silently erase an investor's whole history
        // the first time an older call site saved a headline.
        ...(Array.isArray(data.previousRoles)
          ? { previous_roles: cleanRoles(data.previousRoles) }
          : {}),
      };

    case 'thesis':
      return {
        thesis_statement: trimmedOrNull(data.thesisStatement),
        sweet_spot: trimmedOrNull(data.sweetSpot),
        // `sectors` and `investmentFocus` were the same concept stored twice;
        // `investment_focus` is the column that already feeds matching, so it
        // is the single home. See the migration header.
        investment_focus: cleanArray(data.sectors),
        stage_preference: cleanArray(data.stagePreference),
        team_preferences: cleanArray(data.teamPreferences),
        deal_breakers: cleanArray(data.dealBreakers),
      };

    case 'ticketSize':
      return {
        ticket_size_min: intOrNull(data.min) ?? 0,
        ticket_size_max: intOrNull(data.max) ?? 0,
      };

    case 'valueAdd':
      return {
        expertise_areas: cleanArray(data.expertiseAreas),
        value_add: trimmedOrNull(data.description),
        offers_office_hours: Boolean(data.officeHours),
        board_seats: intOrNull(data.boardExperience),
      };

    case 'social':
      return {
        // Stored as a bare handle; the UI renders `twitter.com/<handle>` and
        // the column CHECK rejects anything that is not handle-shaped.
        social_twitter: trimmedOrNull(data.twitter)?.replace(/^@/, '') ?? null,
        social_website: trimmedOrNull(data.website),
        social_calendly: trimmedOrNull(data.calendly),
        social_substack: trimmedOrNull(data.substack),
      };
  }
}

/**
 * Splits a display name for `profiles`, matching how the founder profile
 * writes it (`(startup)/userprofile.tsx`) so the two personas stay consistent.
 */
export function buildInvestorNameUpdate(fullName: string): {
  first_name: string | null;
  last_name: string | null;
} {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  return {
    first_name: parts[0] ?? null,
    last_name: parts.slice(1).join(' ') || null,
  };
}
